import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import getClientAccessTokenOperation from 'owa-service/lib/operation/getClientAccessTokenOperation';
import { getJsonRequestHeader } from 'owa-service/lib/Utils';
import getClientAccessTokenRequest from 'owa-service/lib/factory/getClientAccessTokenRequest';
import Token from 'owa-service/lib/contract/Token';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getAccessTokenforResource, getDelegationTokenForOwa } from 'owa-tokenprovider';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getGuid } from 'owa-guid';
import { getPhysicalRing } from 'owa-config';

const MsaTokenScope = 'liveprofilecard.access';
const LokiAuthTokenId = '94c63fef-13a3-47bc-8074-75af8c65887a';
const LTI_LOGGING_SCENARIO_NAME = 'OwaPeopleLoki_GetLokiAuthTokenOperation';
const AAD_LOGGING_SCENARIO_NAME = 'OwaPeopleLoki_GetLokiAadAuthTokenOperation';

export default async function getLokiAuthTokenAsync(): Promise<string> {
    let token = null;

    if (isConsumer()) {
        // Consumer users get token type based on flight status
        if (isFeatureEnabled('lpc-aadLokiTokens')) {
            token = await getLokiAadAuthTokenAsync();
        }

        // We're using LTI tokens both if we fail to get an STS token (fallback), and if the STS flight is off
        if (!token) {
            token = await getLokiLtiAuthTokenAsync();
        }
    } else {
        // Enterprise users always get an AAD token
        token = await getLokiAadAuthTokenAsync();
    }

    return token;
}

async function getLokiLtiAuthTokenAsync(): Promise<string> {
    const request = {
        Header: getJsonRequestHeader(),
        Body: getClientAccessTokenRequest({
            TokenRequests: [
                {
                    __type: 'TokenRequest:#Exchange',
                    Id: LokiAuthTokenId,
                    TokenType: 'Loki',
                },
            ],
        }),
    };

    const dataPoint = new PerformanceDatapoint(LTI_LOGGING_SCENARIO_NAME);
    try {
        const results = await getClientAccessTokenOperation(request);
        if (results && results.Body.ResponseMessages.Items[0].ResponseClass == 'Success') {
            dataPoint.end();
            return (<Token>(<any>results.Body.ResponseMessages.Items[0]).Token).TokenValue;
        }

        dataPoint.endWithError(
            DatapointStatus.ServerError,
            Error(results.Body.ResponseMessages.Items[0].ResponseCode)
        );

        return null;
    } catch (e) {
        dataPoint.endWithError(DatapointStatus.ClientError, e);
        return null;
    }
}

async function getLokiAadAuthTokenAsync(): Promise<string> {
    const requestId = getGuid();
    const dataPoint = new PerformanceDatapoint(AAD_LOGGING_SCENARIO_NAME);
    dataPoint.addCustomProperty('RequestId', requestId);

    try {
        let token = null;
        if (isConsumer()) {
            token = await getDelegationTokenForOwa(MsaTokenScope, requestId);
        } else {
            const lokiResourceUrl = getLokiResourceUrl();
            token = await getAccessTokenforResource(lokiResourceUrl);
        }

        if (token) {
            dataPoint.end();
            return token;
        }

        dataPoint.endWithError(DatapointStatus.ServerError, Error('Token is empty.'));
        return null;
    } catch (e) {
        dataPoint.endWithError(DatapointStatus.ClientError, Error('Unexpected error.'));
        return null;
    }
}

function getLokiResourceUrl(): string {
    switch (getPhysicalRing()) {
        case 'BlackForest':
            return 'https://loki.delve.office.de/';

        case 'GccHigh':
            return 'https://gcchigh.loki.office365.us/';

        case 'DoD':
            return 'https://dod.loki.office365.us/';

        case 'Gallatin':
            return 'https://loki.office365.cn/';

        default: {
            if (isFeatureEnabled('lpc-isInGccModerate')) {
                return 'https://gcc.loki.delve.office.com/';
            }

            return 'https://loki.delve.office.com/';
        }
    }
}
